/**
 * Hook for managing Recent Activity across ORA agents
 * Provides centralized state management for observations
 */

import { useState, useEffect } from 'react';

export interface Activity {
  id: string;
  date: string;
  title: string;
  description: string;
  status: "SUSTAIN" | "IMPROVE";
  sentiment: "Excited" | "Confused" | "Concerned" | "Angry" | "Curious" | "Happy";
  category: string;
  selected: boolean;
}

const STORAGE_KEY = 'ora_recent_activities';

// Default activities for initial load
const DEFAULT_ACTIVITIES: Activity[] = [
  {
    id: "1",
    date: "Jan 09, 2026",
    title: "Senior leaders withholding information during After Action Reviews (AARs)",
    description: "Senior leaders have been withholding information during After Action Reviews (AARs) to avoid embarrassment, which is undermining transparency, trust, and the quality of organizational learning. This behavior is preventing teams from fully understanding root causes, repeating avoidable mistakes, and missing opportunities to improve mission readiness.",
    status: "IMPROVE",
    sentiment: "Concerned",
    category: "Information Assurance",
    selected: false,
  },
  {
    id: "3",
    date: "Jan 08, 2026",
    title: "Library Community success with Makers space",
    description: "Continue supporting the Makers space innovation initiative. Expand programming to include local high schools, private partnerships, more workshops and community-led sessions. Document success stories to share with other libraries and secure additional funding.",
    status: "SUSTAIN",
    sentiment: "Excited",
    category: "Cross Functional Collaboration",
    selected: false,
  },
  {
    id: "2",
    date: "Jan 07, 2026",
    title: "Participants struggling to navigate the information system and find what they need",
    description: "I saw several participants struggling to navigate the information system during the event. Most were unable to locate and share critical information in a timely manner and mistakes were made. This can compromise data quality, integrity and availability of information. Participants are visibly frustrated and losing confidence in the information system because they can't locate information, training resources, or tools they need to complete basic workflows.",
    status: "IMPROVE",
    sentiment: "Confused",
    category: "Information & Intelligence",
    selected: false,
  },
  {
    id: "4",
    date: "Jan 06, 2026",
    title: "Girlfriend is angry again - feeling stuck",
    description: 'Relationships are called "RE-lationships" for a reason. Humans tend to repeat many of the same patterns. Forgiveness doesn\'t ignore truth and it doesn\'t mean enabling harmful behavior. Most people are not mind readers, so we must learn to communicate and identify wrongdoings. Keeping a mistake or wrongdoing silent is wrong too. Forgiveness isn\'t a one-time act; it\'s a practice that requires humor and humility. And it takes a lifetime. Advice too. Advice in the book of Matthew includes 18:15-17: Confront wrongdoing with love and clarity. Advice from Luke 17:3-4: If someone repents, forgive — but accountability matters.',
    status: "IMPROVE",
    sentiment: "Confused",
    category: "Relationships & Boundaries",
    selected: false,
  },
  {
    id: "5",
    date: "Jan 05, 2026",
    title: "Performance review process needs standardization and improved feedback quality",
    description: "Implement a standardized performance management framework with clear evaluation criteria, regular feedback cycles, and training for all supervisors. Include employee self-assessments, peer feedback options, and development planning. Establish measurable goals. Establish quarterly check-ins in addition to annual reviews to ensure continuous improvement and alignment with organizational objectives.",
    status: "IMPROVE",
    sentiment: "Concerned",
    category: "Performance Management",
    selected: false,
  },
  {
    id: "6",
    date: "Jan 04, 2026",
    title: "Attendance at library Makers space events has grown by 300%",
    description: "Continue supporting the Makers space initiative. Expand programming to include private partnerships, more workshops and community-led sessions. Document success stories to share with other libraries and secure additional funding.",
    status: "SUSTAIN",
    sentiment: "Excited",
    category: "Community Outreach",
    selected: false,
  },
];

/**
 * Extract the first sentence from text to use as title
 * Handles multiple sentence terminators and truncates if too long
 */
function extractTitle(text: string): string {
  // Remove leading/trailing whitespace
  const cleaned = text.trim();
  
  // Find the first sentence (ending with . ! ? or newline)
  const match = cleaned.match(/^[^.!?\n]+[.!?]?/);
  let title = match ? match[0].trim() : cleaned;
  
  // If title is too long (>80 chars), truncate at word boundary
  if (title.length > 80) {
    title = title.substring(0, 77).trim() + '...';
  }
  
  return title;
}

/**
 * Map sentiment emoji labels to Activity sentiment types
 */
function mapSentiment(sentiment: string): Activity['sentiment'] {
  const lowerSentiment = sentiment.toLowerCase();
  if (lowerSentiment.includes('angry')) return 'Angry';
  if (lowerSentiment.includes('confused')) return 'Confused';
  if (lowerSentiment.includes('curious')) return 'Curious';
  if (lowerSentiment.includes('happy') || lowerSentiment.includes('excited')) return 'Happy';
  return 'Confused'; // default fallback
}

/**
 * Map topic to category
 */
function mapCategory(topic: string): string {
  const categoryMap: Record<string, string> = {
    'AI Bias or Risk': 'AI Ethics & Safety',
    'System Performance': 'Technical Performance',
    'Data Quality': 'Data Management',
    'User Experience': 'User Interface',
    'Security Concern': 'Information Security',
    'Other': 'General Observation'
  };
  
  return categoryMap[topic] || topic;
}

export function useRecentActivity() {
  const [activities, setActivities] = useState<Activity[]>(() => {
    // Load from localStorage on initial mount
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.error('Failed to parse stored activities:', e);
          return DEFAULT_ACTIVITIES;
        }
      }
    }
    return DEFAULT_ACTIVITIES;
  });

  // Save to localStorage whenever activities change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(activities));
    }
  }, [activities]);

  /**
   * Add a new observation to Recent Activity
   */
  const addActivity = (params: {
    observation: string;
    additionalContext?: string;
    topic: string;
    actionType: 'IMPROVE' | 'SUSTAIN';
    sentiment: string;
  }) => {
    const { observation, additionalContext, topic, actionType, sentiment } = params;
    
    // Create title from first sentence of observation
    const title = extractTitle(observation);
    
    // Combine observation and additional context for description
    const description = additionalContext 
      ? `${observation}\n\n${additionalContext}`
      : observation;
    
    // Create new activity
    const newActivity: Activity = {
      id: Date.now().toString(),
      date: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }),
      title,
      description,
      status: actionType,
      sentiment: mapSentiment(sentiment),
      category: mapCategory(topic),
      selected: false,
    };
    
    // Add to beginning of array (newest first)
    setActivities(prev => [newActivity, ...prev]);
    
    return newActivity;
  };

  /**
   * Remove an activity by ID
   */
  const removeActivity = (id: string) => {
    setActivities(prev => prev.filter(activity => activity.id !== id));
  };

  /**
   * Toggle selection of an activity
   */
  const toggleSelection = (id: string) => {
    setActivities(prev => 
      prev.map(activity => 
        activity.id === id 
          ? { ...activity, selected: !activity.selected }
          : activity
      )
    );
  };

  /**
   * Select all activities
   */
  const selectAll = () => {
    setActivities(prev => 
      prev.map(activity => ({ ...activity, selected: true }))
    );
  };

  /**
   * Deselect all activities
   */
  const deselectAll = () => {
    setActivities(prev => 
      prev.map(activity => ({ ...activity, selected: false }))
    );
  };

  /**
   * Clear all activities (reset to defaults)
   */
  const clearAll = () => {
    setActivities(DEFAULT_ACTIVITIES);
  };

  return {
    activities,
    addActivity,
    removeActivity,
    toggleSelection,
    selectAll,
    deselectAll,
    clearAll,
    setActivities,
  };
}
